import {
  BREAK_POLICY_MIN_BREAK_DURATION_IN_MINUTES,
  BREAK_POLICY_MIN_SHIFT_DURATION_IN_MINUTES,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { Alert, FormControl, MenuItem, Select, type SelectChangeEvent, Stack } from "@mui/material";
import { type BreakPolicyRule } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import { useEffect, useState } from "react";

import { Button } from "../../../../components/Button";
import { IconButton } from "../../../../components/IconButton";
import { LoadingButton } from "../../../../components/LoadingButton";
import { Text } from "../../../../components/Text";
import {
  BREAK_DURATION_MINUTE_OPTIONS,
  MAX_BREAK_POLICY_RULES,
  SHIFT_DURATION_HOUR_OPTIONS,
} from "./constants";
import { sortBreakPolicyRules, validateBreakPolicyRules } from "./utils";

type BreakPolicyRuleDraft = Partial<BreakPolicyRule>;

interface BreakPolicyEditorProps {
  savedRules: BreakPolicyRule[] | undefined;
  isSaving: boolean;
  onSave: (rules: BreakPolicyRule[]) => Promise<boolean>;
}

function buildInitialRules(savedRules: BreakPolicyRule[] | undefined): BreakPolicyRuleDraft[] {
  if (isDefined(savedRules) && savedRules.length > 0) {
    return sortBreakPolicyRules(savedRules);
  }

  return [
    {
      minShiftDurationInMinutes: BREAK_POLICY_MIN_SHIFT_DURATION_IN_MINUTES,
      breakDurationInMinutes: BREAK_POLICY_MIN_BREAK_DURATION_IN_MINUTES,
    },
  ];
}

export function BreakPolicyEditor({ savedRules, isSaving, onSave }: BreakPolicyEditorProps) {
  const [rules, setRules] = useState<BreakPolicyRuleDraft[]>(() => buildInitialRules(savedRules));
  const [error, setError] = useState<string | undefined>(undefined);

  // Re-sync local state from server data after save / external refetch.
  useEffect(() => {
    setRules(buildInitialRules(savedRules));
  }, [savedRules]);

  const updateRule = (index: number, newRule: Partial<BreakPolicyRuleDraft>) => {
    setRules((previous) =>
      previous.map((rule, draftIndex) => (draftIndex === index ? { ...rule, ...newRule } : rule))
    );
    setError(undefined);
  };

  const addRule = () => {
    setRules((previous) => [...previous, {}]);
    setError(undefined);
  };

  const removeRule = (index: number) => {
    setRules((previous) => previous.filter((_, ruleIndex) => ruleIndex !== index));
    setError(undefined);
  };

  const handleSave = async () => {
    const result = validateBreakPolicyRules(rules);
    if (!result.ok) {
      setError(result.message);
      return;
    }

    setError(undefined);
    await onSave(result.rules);
  };

  const extendedRules = rules.slice(1);

  return (
    <Stack spacing={3}>
      <RuleFormRow
        rule={rules[0]}
        disabled={isSaving}
        onChange={(partial) => updateRule(0, partial)}
      />

      <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
        Add any additional breaks for longer shift lengths here. When selected, workers will be
        required to take the set break length for the shift length defined below.
      </Text>

      {isDefined(error) && (
        <Alert severity="error" role="alert">
          {error}
        </Alert>
      )}

      {extendedRules.map((draft, extendedIndex) => {
        const index = extendedIndex + 1;
        return (
          <RuleFormRow
            key={index}
            rule={draft}
            disabled={isSaving}
            onChange={(partial) => updateRule(index, partial)}
            onRemove={() => removeRule(index)}
          />
        );
      })}

      <Stack direction="row">
        <Button
          variant="outlined"
          size="small"
          disabled={rules.length >= MAX_BREAK_POLICY_RULES || isSaving}
          onClick={addRule}
        >
          Add Extended Break Rule
        </Button>
      </Stack>

      <Stack direction="row">
        <LoadingButton isLoading={isSaving} variant="primary" size="small" onClick={handleSave}>
          Save
        </LoadingButton>
      </Stack>
    </Stack>
  );
}

interface RuleFormRowProps {
  rule: BreakPolicyRuleDraft;
  disabled: boolean;
  onChange: (partial: Partial<BreakPolicyRuleDraft>) => void;
  onRemove?: () => void;
}

function RuleFormRow({ rule, disabled, onChange, onRemove }: RuleFormRowProps) {
  const handleShiftChange = (event: SelectChangeEvent<number | string>) => {
    const { value } = event.target;
    onChange({
      minShiftDurationInMinutes: value === "" ? undefined : Math.round(Number(value) * 60),
    });
  };

  const handleBreakChange = (event: SelectChangeEvent<number | string>) => {
    const { value } = event.target;
    onChange({ breakDurationInMinutes: value === "" ? undefined : Number(value) });
  };

  const shiftHoursValue = isDefined(rule.minShiftDurationInMinutes)
    ? rule.minShiftDurationInMinutes / 60
    : "";
  const breakValue = rule.breakDurationInMinutes ?? "";

  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Stack
        useFlexGap
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", md: "center" }}
        flexWrap="wrap"
        sx={{ flex: 1, minWidth: 0 }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Text variant="body2">For shifts that exceed</Text>
          <FormControl size="small" sx={{ "& .MuiSelect-select": { paddingY: 0 } }}>
            <Select
              displayEmpty
              disabled={disabled}
              inputProps={{ "aria-label": "Shift length in hours" }}
              value={shiftHoursValue}
              onChange={handleShiftChange}
            >
              <MenuItem disabled value="">
                <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
                  Select
                </Text>
              </MenuItem>
              {SHIFT_DURATION_HOUR_OPTIONS.map((hours) => (
                <MenuItem key={hours} value={hours}>
                  {Number.isInteger(hours) ? `${hours}` : hours.toFixed(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Text variant="body2">hours,</Text>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <Text variant="body2">required break is</Text>
          <FormControl size="small" sx={{ "& .MuiSelect-select": { paddingY: 0 } }}>
            <Select
              displayEmpty
              disabled={disabled}
              inputProps={{ "aria-label": "Break length in minutes" }}
              value={breakValue}
              onChange={handleBreakChange}
            >
              <MenuItem disabled value="">
                <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
                  Select
                </Text>
              </MenuItem>
              {BREAK_DURATION_MINUTE_OPTIONS.map((minutes) => (
                <MenuItem key={minutes} value={minutes}>
                  {minutes}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Text variant="body2">minutes</Text>
        </Stack>
      </Stack>
      {isDefined(onRemove) && (
        <IconButton
          aria-label="Remove break rule"
          iconType="close"
          size="small"
          variant="outlined"
          disabled={disabled}
          onClick={onRemove}
        />
      )}
    </Stack>
  );
}
